import { Box, Typography } from "@mui/material";
import React, { useLayoutEffect, useRef, useState } from "react";

interface ReviewSnippetProps {
  response: {
    likemost: string;
  };
}

export default function ReviewSnippet({ response }: ReviewSnippetProps) {
  const containerRef = useRef<HTMLParagraphElement>(null);
  // Separate ref to target only the text content span
  const textSpanRef = useRef<HTMLSpanElement>(null);

  const [truncatedText, setTruncatedText] = useState<string>(response.likemost);

  // Line height configuration
  const LINE_HEIGHT = 18.6;
  const MAX_HEIGHT = LINE_HEIGHT * 3.5; // ~56px

  useLayoutEffect(() => {
    const container = containerRef.current;
    const textSpan = textSpanRef.current;
    if (!container || !textSpan) return;

    const fullText = response.likemost
      ? response.likemost.replaceAll("\n", " ")
      : "";

    const handleResize = () => {
      // 1. Reset text to full length to accurately measure bounds
      setTruncatedText(fullText);

      requestAnimationFrame(() => {
        // If the entire container (Text + Primary Link) fits inside 3 lines, stop here
        if (container.scrollHeight <= MAX_HEIGHT) {
          return;
        }

        const words = fullText.split(" ");

        // 2. Pop words strictly from the text span.
        // This leaves the standalone MUI "See more" element completely untouched and perfectly colored.
        while (container.scrollHeight > MAX_HEIGHT && words.length > 0) {
          words.pop();
          textSpan.innerText = words.join(" ") + "...";
        }

        // 3. Commit the perfectly calculated snapshot back into React state
        setTruncatedText(words.join(" ") + "...");
      });
    };

    const resizeObserver = new ResizeObserver(handleResize);
    resizeObserver.observe(container);

    return () => resizeObserver.disconnect();
  }, [response.likemost]);

  return (
    <Box
      my={1.5}
      sx={{
        display: "flex",
        flexDirection: "column",
        alignItems: "stretch",
      }}
    >
      <Typography
        ref={containerRef}
        variant="bodySmall"
        color="onSurface.main"
        component="p"
        sx={{
          maxHeight: 56,
          lineHeight: `${LINE_HEIGHT}px`,
          overflow: "hidden",
          display: "block",
        }}
      >
        {/* The DOM text manipulation happens safely inside this isolated span */}
        <span ref={textSpanRef}>{truncatedText}</span>

        {/* 
          This remains a native, untouched MUI component. 
          It always displays, always stays inline, and never loses its "primary" color.
        */}
        <Typography
          component="span"
          variant="bodySmall"
          color="primary"
          sx={{
            textDecoration: "underline",
            cursor: "pointer",
            marginLeft: "6px",
            display: "inline",
            whiteSpace: "nowrap",
          }}
          onClick={() => {
            /* Handle text expansion or navigation routing here */
          }}
        >
          See more
        </Typography>
      </Typography>
    </Box>
  );
}
